import LockIcon from '@mui/icons-material/Lock';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * PfeSktSwitch - A reusable component for switching between PFE and SKT modes
 *
 * @param {string} activeTab - Current active tab: 'PFE' or 'SKT'
 * @param {function} onSwitch - Callback when tab is switched, receives 'PFE' or 'SKT'
 * @param {string} variant - Style variant: 'segmented' (StudyMain style) or 'toggle' (DailyQuiz style)
 * @param {string} size - Size variant: 'sm', 'md', 'lg'
 * @param {boolean} sktLocked - Whether SKT option is locked (shows lock icon)
 * @param {object} labels - Custom labels: { pfe: 'PFE', skt: 'SKT' }
 */
class PfeSktSwitch extends React.Component {
  handleClick = (tab) => {
    const { activeTab, onSwitch, sktLocked } = this.props;

    // Don't allow switching to SKT if locked
    if (tab === 'SKT' && sktLocked) return;

    // Don't call if already active
    if (tab === activeTab) return;

    onSwitch(tab);
  };

  renderSegmented() {
    const { activeTab, size, sktLocked, labels } = this.props;
    const isSkt = activeTab === 'SKT';

    return (
      <div className={`pfe-skt-switch segmented size-${size}`}>
        <div className={`switch-bg ${isSkt ? 'right' : 'left'}`} />

        <button
          className={`switch-option ${!isSkt ? 'active' : ''}`}
          onClick={() => this.handleClick('PFE')}
        >
          {labels.pfe}
        </button>

        <button
          className={`switch-option ${isSkt ? 'active' : ''}`}
          disabled={sktLocked}
          onClick={() => this.handleClick('SKT')}
        >
          <span className="option-label">{labels.skt}</span>
          {sktLocked && (
            <span className="lock-overlay">
              <LockIcon />
            </span>
          )}
        </button>
      </div>
    );
  }

  renderToggle() {
    const { activeTab, size, sktLocked, labels } = this.props;

    return (
      <div className={`pfe-skt-switch toggle size-${size}`}>
        <button
          className={`toggle-btn ${activeTab === 'PFE' ? 'active' : ''}`}
          onClick={() => this.handleClick('PFE')}
        >
          {labels.pfe}
        </button>

        <button
          className={`toggle-btn ${activeTab === 'SKT' ? 'active' : ''}`}
          disabled={sktLocked}
          onClick={() => this.handleClick('SKT')}
        >
          {labels.skt}
          {sktLocked && (
            <span className="lock-icon">
              <LockIcon />
            </span>
          )}
        </button>
      </div>
    );
  }

  render() {
    const { variant } = this.props;

    if (variant === 'segmented') {
      return this.renderSegmented();
    }

    return this.renderToggle();
  }
}

PfeSktSwitch.propTypes = {
  activeTab: PropTypes.oneOf(['PFE', 'SKT']).isRequired,
  onSwitch: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['segmented', 'toggle']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  sktLocked: PropTypes.bool,
  labels: PropTypes.shape({
    pfe: PropTypes.string,
    skt: PropTypes.string,
  }),
};

PfeSktSwitch.defaultProps = {
  variant: 'toggle',
  size: 'md',
  sktLocked: false,
  labels: {
    pfe: 'PFE',
    skt: 'SKT',
  },
};

export default PfeSktSwitch;
